import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  FileText,
  Users,
  Settings as SettingsIcon,
  Plus,
  Search,
  ChevronRight,
  RefreshCw,
  ArrowUpRight,
  WifiOff,
  X,
  ChartNoAxesColumn,
} from "lucide-react";
import {
  type Bootstrap,
  type DocumentRecord,
  type Draft,
  type Client,
  newDraft,
  money,
  dateLabel,
  statusLabel,
  kindLabels,
} from "../shared/domain";
import { Login } from "./components/Login";
import { ApiError, api, errorMessage } from "./api";
import { Notice, Spinner } from "./components/Common";
import { Editor } from "./components/Editor";
import { recoveries, removeRecovery, saveRecovery } from "./recovery";
import { Customers, CustomerForm } from "./components/Customers";
import { Settings } from "./components/Settings";
import { Overview } from "./components/Overview";
import { defaultOverview, overviewSearch } from "../shared/overview";
import { DocumentDetail } from "./components/DocumentDetail";
import { useNavigation, useKeyboardViewport, type Tab } from "./navigation";
export default function App() {
  const navigation = useNavigation();
  useKeyboardViewport();
  const [customerQuery, setCustomerQuery] = useState("");
  const [addedCustomer, setAddedCustomer] = useState<Client | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [listRevision, setListRevision] = useState(0);
  const documentCache = useRef(new Map<string, DocumentRecord | Draft>());
  const lastList = useRef("");
  const [data, setData] = useState<Bootstrap | null>(null),
    [error, setError] = useState(""),
    [tab, setTab] = useState<Tab>("invoices"),
    [draft, setDraft] = useState<Draft | null>(null),
    [selected, setSelected] = useState<DocumentRecord | null>(null),
    [customer, setCustomer] = useState<Client | null | undefined>(undefined),
    [query, setQuery] = useState(""),
    [filter, setFilter] = useState("all"),
    [docs, setDocs] = useState<DocumentRecord[]>([]),
    [hasMore, setHasMore] = useState(false),
    [searching, setSearching] = useState(false),
    [offline, setOffline] = useState(!navigator.onLine),
    [toast, setToast] = useState(""),
    [recovery, setRecovery] = useState<Draft | null>(null),
    [refreshing, setRefreshing] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const searchSequence = useRef(0);
  const load = useCallback(async () => {
    const b = await api<Bootstrap>("/bootstrap");
    setData(b);
    setNeedsLogin(false);
    setError("");
    return b;
  }, []);
  useEffect(() => {
    void load().catch((e) => {
      if (e instanceof ApiError && e.status === 401) setNeedsLogin(true);
      else setError(errorMessage(e));
    });
    const online = () => setOffline(false),
      off = () => setOffline(true);
    window.addEventListener("online", online);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", off);
    };
  }, [load]);
  const authenticated = !!data;
  useLayoutEffect(() => {
    if (!authenticated) return;
    const route = navigation.route;
    setTab(route.tab);
    setError("");
    setCustomer(undefined);
    setAddedCustomer(null);
    setDraft(null);
    setSelected(null);
    if (!route.document) {
      setRouteLoading(false);
      return;
    }
    const cached = documentCache.current.get(route.document);
    const apply = (value: DocumentRecord | Draft) => {
      if ("status" in value) {
        documentCache.current.set(value.id, value);
        value.status === "draft" ? setDraft(value.draft) : setSelected(value);
      } else setDraft(value);
      setRouteLoading(false);
    };
    if (cached) {
      apply(cached);
      return;
    }
    // Unsaved routes survive a reload using the device recovery slot.
    let recovered: Draft | undefined;
    try {
      recovered = recoveries(data!.user.email).find(
        (d) => d.id === route.document,
      );
    } catch {}
    if (recovered?.version === 0) {
      documentCache.current.set(recovered.id, recovered);
      apply(recovered);
      return;
    }
    let cancelled = false;
    setRouteLoading(true);
    void api<DocumentRecord>(`/documents/${encodeURIComponent(route.document)}`)
      .then((d) => {
        if (!cancelled) apply(d);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(errorMessage(e));
          setRouteLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [navigation.route, authenticated]);
  useLayoutEffect(() => {
    navigation.restore(
      authenticated &&
        !routeLoading &&
        (navigation.route.document
          ? (draft?.id || selected?.id) === navigation.route.document
          : !draft &&
            !selected &&
            tab === navigation.route.tab &&
            tab !== "overview"),
    );
  });
  useEffect(() => {
    if (data && !draft) {
      try {
        setRecovery(recoveries(data.user.email)[0] || null);
      } catch {
        setRecovery(null);
      }
    }
  }, [data, draft]);
  useEffect(() => {
    if (!data || tab !== "invoices" || draft || selected) return;
    const listKey = JSON.stringify([query, filter, listRevision]);
    if (lastList.current === listKey) return;
    const sequence = ++searchSequence.current;
    setSearching(true);
    const timer = setTimeout(
      () => {
        void api<{ documents: DocumentRecord[]; hasMore: boolean }>(
          `/documents?q=${encodeURIComponent(query)}&filter=${filter}`,
        )
          .then((r) => {
            if (sequence === searchSequence.current) {
              lastList.current = listKey;
              setDocs(r.documents);
              setHasMore(r.hasMore);
            }
          })
          .catch((e) => {
            if (sequence === searchSequence.current) setError(errorMessage(e));
          })
          .finally(() => {
            if (sequence === searchSequence.current) setSearching(false);
          });
      },
      query ? 180 : 0,
    );
    return () => {
      clearTimeout(timer);
      ++searchSequence.current;
    };
  }, [data, query, filter, tab, draft, selected, listRevision]);
  useEffect(() => {
    if (toast) {
      const id = setTimeout(() => setToast(""), 5000);
      return () => clearTimeout(id);
    }
  }, [toast]);
  const selectTab = (next: Tab) => {
    if (draft) return;
    if (next === tab && !selected && !navigation.route.document) return;
    navigation.navigate(
      { tab: next, document: null, edit: false },
      false,
      "tab",
    );
  };
  const openDoc = (d: DocumentRecord) => {
    documentCache.current.set(d.id, d);
    navigation.navigate({
      tab: "invoices",
      document: d.id,
      edit: d.status === "draft",
    });
  };
  const openDraft = (value: Draft, replace = false) => {
    documentCache.current.set(value.id, value);
    navigation.navigate(
      { tab: "invoices", document: value.id, edit: true },
      replace,
    );
  };
  const refresh = async () => {
    setRefreshing(true);
    try {
      await load();
      setListRevision((n) => n + 1);
      if (selected) {
        const latest = await api<DocumentRecord>(`/documents/${selected.id}`);
        documentCache.current.set(latest.id, latest);
        setSelected(latest);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setRefreshing(false);
    }
  };
  const startNew = () =>
    openDraft(newDraft(data?.business.defaultVatBasisPoints));
  if (needsLogin) return <Login onSignedIn={load} />;
  return (
    <div className={`app-shell ${draft ? "editing" : ""}`}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <aside className="sidebar">
        <a
          className="brand"
          href="/"
          aria-label="AI Link Logistics home"
          onClick={(e) => {
            e.preventDefault();
            selectTab("invoices");
          }}
        >
          <img src="/brand-mark.svg" alt="" width={40} height={40} />
          <span>
            <strong>Link Logistics</strong>
          </span>
        </a>
        <div className="sidebar-rule" />
        <nav aria-label="Main navigation">
          {(
            [
              ["invoices", FileText, "Invoices"],
              ["overview", ChartNoAxesColumn, "Overview"],
              ["customers", Users, "Customers"],
              ["settings", SettingsIcon, "Settings"],
            ] as const
          ).map(([id, Icon, label]) => (
            <button
              key={id}
              onClick={() => selectTab(id)}
              className={tab === id ? "active" : ""}
              aria-current={tab === id ? "page" : undefined}
              disabled={!!draft}
            >
              <Icon size={21} />
              <span>{label}</span>
              {id === "invoices" && data && data.stats.draftCount > 0 && (
                <span className="nav-count">{data.stats.draftCount}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <span>
            Every delivery.
            <br />
            Every detail.
          </span>
        </div>
      </aside>
      <div className="main-shell">
        <header className="topbar">
          <a
            href="/"
            className="mobile-brand"
            aria-label="AI Link Logistics home"
            onClick={(e) => {
              e.preventDefault();
              selectTab("invoices");
            }}
          >
            <img src="/brand-mark.svg" alt="" />
            <strong>Link Logistics</strong>
          </a>
          <span className="workspace-label">
            LINK LOGISTICS <span>/</span> PRIVATE WORKSPACE
          </span>
          <div className="topbar-actions">
            {data?.user.local && (
              <span className="local-label">Local preview</span>
            )}
            <button
              className="icon-button"
              aria-label="Refresh shared records"
              disabled={refreshing || !!draft}
              onClick={() => void refresh()}
            >
              <RefreshCw size={18} className={refreshing ? "spinner" : ""} />
            </button>
            <span className="avatar" aria-label={data?.user.email || "Account"}>
              AL
            </span>
          </div>
        </header>
        {offline && (
          <div className="offline-banner" role="status">
            <WifiOff size={16} />
            You’re offline. Draft changes stay on this device.
          </div>
        )}
        <main id="main" tabIndex={-1}>
          <div
            className={`screen-motion motion-${navigation.motion}`}
            key={`${navigation.route.tab}:${navigation.route.document || "list"}`}
          >
            {routeLoading ? (
              <div
                className="document-skeleton"
                role="status"
                aria-label="Opening invoice"
              >
                <span />
                <span />
                <span />
                <span />
              </div>
            ) : !data ? (
              <div className="loading-state">
                {error ? (
                  <>
                    <h1>Let’s get connected</h1>
                    <Notice type="error">{error}</Notice>
                    <button
                      className="primary"
                      onClick={() => location.reload()}
                    >
                      Try again
                    </button>
                  </>
                ) : (
                  <>
                    <Spinner />
                    <p>Opening your workspace…</p>
                  </>
                )}
              </div>
            ) : draft ? (
              <Editor
                key={draft.id}
                initial={draft}
                data={data}
                onClose={() => {
                  setListRevision((n) => n + 1);
                  navigation.back();
                  void load();
                }}
                onCopy={(value) => {
                  try {
                    saveRecovery(data.user.email, value);
                    removeRecovery(data.user.email, draft.id);
                  } catch {}
                  openDraft(value, true);
                }}
                addedCustomer={addedCustomer}
                onSaved={(d) => {
                  documentCache.current.set(d.id, d);
                  setData((b) =>
                    b
                      ? {
                          ...b,
                          documents: [
                            d,
                            ...b.documents.filter((x) => x.id !== d.id),
                          ],
                        }
                      : b,
                  );
                  setListRevision((n) => n + 1);
                }}
                onIssued={(d) => {
                  documentCache.current.set(d.id, d);
                  navigation.navigate(
                    { tab: "invoices", document: d.id, edit: false },
                    true,
                  );
                  setListRevision((n) => n + 1);
                  setToast(`${d.number} is ready to share`);
                  void load();
                }}
                onCustomer={() => setCustomer(null)}
              />
            ) : selected ? (
              <DocumentDetail
                key={selected.id}
                doc={selected}
                backLabel={tab === "overview" ? "Overview" : "Invoices"}
                onBack={navigation.back}
                onChange={(d) => {
                  documentCache.current.set(d.id, d);
                  setSelected(d);
                  setListRevision((n) => n + 1);
                  void load();
                }}
                onEdit={(d) => {
                  openDraft(d.draft);
                }}
              />
            ) : (
              <>
                {error && (
                  <Notice type="error">
                    {error}
                    <button
                      className="text-button"
                      onClick={() => void refresh()}
                    >
                      Try again
                    </button>
                  </Notice>
                )}
                {tab === "overview" ? (
                  <Overview
                    key={overviewSearch(
                      navigation.route.overview || defaultOverview,
                    )}
                    onReportBack={() => {
                      const previous = navigation.previousRoute;
                      if (
                        previous?.tab === "overview" &&
                        !previous.document &&
                        !previous.overview?.scope
                      )
                        navigation.back();
                      else
                        navigation.navigate(
                          {
                            tab: "overview",
                            document: null,
                            edit: false,
                            overview: {
                              ...(navigation.route.overview || defaultOverview),
                              scope: undefined,
                              client: undefined,
                              clientName: undefined,
                            },
                          },
                          true,
                          "back",
                        );
                    }}
                    onReady={() => navigation.restore(true)}
                    query={navigation.route.overview || defaultOverview}
                    revision={listRevision}
                    onQuery={(overview) =>
                      navigation.navigate(
                        {
                          tab: "overview",
                          document: null,
                          edit: false,
                          overview,
                        },
                        false,
                        "tab",
                      )
                    }
                    onDocument={(id) =>
                      navigation.navigate({
                        ...navigation.route,
                        document: id,
                        edit: false,
                      })
                    }
                    onNew={startNew}
                  />
                ) : tab === "customers" ? (
                  <Customers
                    clients={data.clients}
                    query={customerQuery}
                    onQuery={setCustomerQuery}
                    onEdit={setCustomer}
                    onNew={() => setCustomer(null)}
                  />
                ) : tab === "settings" ? (
                  <Settings
                    data={data}
                    onRefresh={async () => {
                      await load();
                    }}
                  />
                ) : (
                  <>
                    <div className="page-title ledger-title">
                      <div>
                        <h1>Invoices</h1>
                        <p className="muted desktop-subtitle">
                          A clear record of the work you’ve delivered.
                        </p>
                      </div>
                      <button
                        className="primary desktop-action"
                        onClick={startNew}
                      >
                        <Plus size={20} />
                        New invoice
                      </button>
                    </div>
                    <button
                      className="primary mobile-action"
                      onClick={startNew}
                    >
                      <Plus size={20} />
                      New invoice
                    </button>
                    <button
                      type="button"
                      className="workspace-summary"
                      aria-label="View outstanding invoices in Overview"
                      onClick={() =>
                        navigation.navigate({
                          tab: "overview",
                          document: null,
                          edit: false,
                          overview: { period: "month", scope: "outstanding" },
                        })
                      }
                    >
                      <div>
                        <span className="summary-label">Outstanding</span>
                        <strong>{money(data.stats.outstandingPence)}</strong>
                      </div>
                      <div className="summary-secondary">
                        <span>
                          {data.stats.overdueCount > 0 ? (
                            <>
                              <b>{data.stats.overdueCount}</b> overdue
                            </>
                          ) : (
                            "No overdue invoices"
                          )}
                        </span>
                        <span>
                          <b>{data.stats.draftCount}</b>{" "}
                          {data.stats.draftCount === 1 ? "draft" : "drafts"}
                        </span>
                      </div>
                      <span className="summary-note">
                        Across your issued invoices
                        <ArrowUpRight size={16} />
                      </span>
                    </button>
                    {recovery && (
                      <div className="recovery-banner">
                        <div>
                          <strong>A draft is saved on this device</strong>
                          <span>Continue where you left off.</span>
                        </div>
                        <button
                          className="text-button"
                          onClick={() => openDraft(recovery)}
                        >
                          Resume <ChevronRight size={16} />
                        </button>
                        <button
                          className="icon-button"
                          aria-label="Dismiss recovered draft"
                          onClick={() => {
                            removeRecovery(data.user.email, recovery.id);
                            setRecovery(recoveries(data.user.email)[0] || null);
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                    <div className="list-toolbar">
                      <div
                        className="filter-tabs"
                        role="group"
                        aria-label="Filter documents"
                      >
                        {[
                          ["all", "All"],
                          ["unpaid", "Unpaid"],
                          ["draft", "Drafts"],
                          ["trash", "Trash"],
                        ].map(([id, label]) => (
                          <button
                            key={id}
                            aria-pressed={filter === id}
                            className={filter === id ? "active" : ""}
                            onClick={() => setFilter(id)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <label className="search">
                        <Search size={18} />
                        <input
                          type="search"
                          placeholder="Search customer or invoice"
                          aria-label="Search customer or invoice"
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                        />
                      </label>
                    </div>
                    <div className="ledger" aria-busy={searching}>
                      <div className="ledger-heading">
                        <span>Customer & document</span>
                        <span>Issued</span>
                        <span>Status</span>
                        <span>Amount</span>
                        <span />
                      </div>
                      {searching && !docs.length ? (
                        <div
                          className="ledger-skeleton"
                          role="status"
                          aria-label="Loading invoices"
                        >
                          {[0, 1, 2, 3].map((i) => (
                            <div key={i}>
                              <span />
                              <span />
                            </div>
                          ))}
                        </div>
                      ) : docs.length ? (
                        docs.map((d) => {
                          const status = statusLabel(d, data.today);
                          return (
                            <button
                              className="invoice-row"
                              key={d.id}
                              onClick={() => openDoc(d)}
                            >
                              <span className="row-customer">
                                <strong>{d.clientName}</strong>
                                <span className="document-number">
                                  {d.number || "Draft"}
                                  {d.kind !== "invoice" && (
                                    <span className="kind-label">
                                      {" "}
                                      · {kindLabels[d.kind]}
                                    </span>
                                  )}
                                </span>
                              </span>
                              <span className="row-date">
                                {dateLabel(d.issueDate, true)}
                              </span>
                              <span
                                className={`badge ${status.toLowerCase().replaceAll(" ", "-")}`}
                              >
                                {status}
                              </span>
                              <strong className="row-amount">
                                {money(d.totalPence)}
                              </strong>
                              <ChevronRight className="row-chevron" size={18} />
                            </button>
                          );
                        })
                      ) : (
                        <div className="empty-state">
                          <FileText size={36} />
                          <h2>
                            {searching
                              ? "Finding your documents…"
                              : query
                                ? "No matching documents"
                                : filter === "trash"
                                  ? "Your trash is empty"
                                  : filter === "unpaid"
                                    ? "All caught up"
                                    : filter === "draft"
                                      ? "No drafts in progress"
                                      : "Your first invoice starts here"}
                          </h2>
                          <p>
                            {query
                              ? "Try a customer name or part of an invoice number."
                              : filter === "unpaid"
                                ? "You have no outstanding issued invoices."
                                : filter === "trash"
                                  ? "Deleted drafts can be restored here."
                                  : "Choose a customer, add your services, then review and share."}
                          </p>
                          {filter !== "trash" && !query && (
                            <button className="primary" onClick={startNew}>
                              <Plus size={18} />
                              New invoice
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {hasMore && (
                      <button
                        className="secondary load-more"
                        disabled={searching}
                        onClick={async () => {
                          setSearching(true);
                          try {
                            const r = await api<{
                              documents: DocumentRecord[];
                              hasMore: boolean;
                            }>(
                              `/documents?q=${encodeURIComponent(query)}&filter=${filter}&offset=${docs.length}`,
                            );
                            setDocs((d) => [...d, ...r.documents]);
                            setHasMore(r.hasMore);
                          } catch (e) {
                            setError(errorMessage(e));
                          } finally {
                            setSearching(false);
                          }
                        }}
                      >
                        Load more documents
                      </button>
                    )}
                    <p className="ledger-footnote">
                      Amounts include VAT. Service dates are shown inside each
                      document.
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      {!draft && (
        <nav className="bottom-nav" aria-label="Mobile navigation">
          {(
            [
              ["invoices", FileText, "Invoices"],
              ["overview", ChartNoAxesColumn, "Overview"],
              ["customers", Users, "Customers"],
              ["settings", SettingsIcon, "Settings"],
            ] as const
          ).map(([id, Icon, label]) => (
            <button
              key={id}
              className={tab === id ? "active" : ""}
              aria-current={tab === id ? "page" : undefined}
              onClick={() => selectTab(id)}
            >
              <Icon size={21} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      )}
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
      {customer !== undefined && (
        <CustomerForm
          initial={customer}
          onClose={() => setCustomer(undefined)}
          onSave={(c) => {
            if (draft) setAddedCustomer(c);
            setData((b) =>
              b
                ? {
                    ...b,
                    clients: [
                      c,
                      ...b.clients.filter((x) => x.id !== c.id),
                    ].sort((a, b) =>
                      a.businessName.localeCompare(b.businessName),
                    ),
                  }
                : b,
            );
            setCustomer(undefined);
            setToast("Customer saved");
          }}
        />
      )}
    </div>
  );
}
